import { useState } from "react";
import { OrderPage_OrderFragment } from "../../../codegen/graphql";
import { formatCurrency } from "../../utils/currency";
import dayjs from "dayjs";
import { Button } from "../common/Button";
import AddOrEditAfterSaleModal from "./AddOrEditAfterSaleModal";

type AfterSale = NonNullable<OrderPage_OrderFragment['afterSales']>[0]

export default function OrderPageAfterSales({
  order,
}: {
  order: OrderPage_OrderFragment;
}) {
  const [modalAfterSaleId, setModalAfterSaleId] = useState<string | null | undefined>(undefined);

  const openAddModal = () => setModalAfterSaleId(null);
  const openEditModal = (id: string) => setModalAfterSaleId(id);
  const closeModal = () => setModalAfterSaleId(undefined);

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="flex justify-end px-4 py-2 border-b border-gray-200 bg-gray-50">
        <Button label="Add after sale" preset="primary" size="sm" onClick={openAddModal} />
      </div>
      {/* Header Row */}
      <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200">
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Line item
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Type
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Status
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Return Qty
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Received Qty
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Restocked Qty
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Return Package
        </div>
        <div className="col-span-1 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
          Label Cost
        </div>
        <div className="col-span-1 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
          Requested
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Created
        </div>
        <div className="col-span-1 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Updated
        </div>
        <div className="col-span-1 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
          Actions
        </div>
      </div>

      {/* Data Rows */}
      <div className="bg-white">
        {!order.afterSales || order.afterSales.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No after sales found for this order.
            <div className="mt-2">
              <Button label="Add after sale" preset="primary" size="sm" onClick={openAddModal} />
            </div>
          </div>
        ) : (
          order.afterSales.map((afterSale) => {
            const lineItem = order.orderProductsVariations?.find(
              (opv) => String(opv.id) === String(afterSale.lineItemId)
            );
            return (
            <div
              key={afterSale.id}
              className="grid grid-cols-12 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="col-span-1 px-4 py-4 flex items-center gap-2">
                {lineItem ? (
                  <>
                    {lineItem.adVariation?.mainImage?.url ? (
                      <img
                        src={lineItem.adVariation.mainImage.url}
                        alt=""
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <span className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">—</span>
                    )}
                    <span className="text-sm text-gray-900">x{lineItem.quantity ?? '—'}</span>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">—</span>
                )}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.type || '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.status || '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.lineItemQuantity ?? '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.restockedQuantity ?? '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.requestedAt
                  ? dayjs(afterSale.requestedAt).format('DD MMM YYYY, HH:mm')
                  : '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.createdAt 
                  ? dayjs(afterSale.createdAt).format('DD MMM YYYY, HH:mm')
                  : '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-gray-900">
                {afterSale.updatedAt
                  ? dayjs(afterSale.updatedAt).format('DD MMM YYYY, HH:mm')
                  : '-'}
              </div>
              <div className="col-span-1 px-4 py-4 text-sm text-right">
                <Button
                  label="Edit"
                  preset="white"
                  size="sm"
                  onClick={() => openEditModal(afterSale.id)}
                />
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Additional Details Section - extra informational fields */}
      {order.afterSales && order.afterSales.length > 0 && (
        <div className="bg-gray-50 border-t border-gray-200">
          {order.afterSales.map((afterSale: AfterSale) => (
            <div
              key={`details-${afterSale.id}`}
              className="grid grid-cols-12 border-b border-gray-200 px-6 py-3"
            >
              <div className="col-span-12">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  {afterSale.externalId && (
                    <div>
                      <span className="font-medium text-gray-700">External ID:</span>{' '}
                      <span className="text-gray-900">{afterSale.externalId}</span>
                    </div>
                  )}
                  {(afterSale.reason || afterSale.reasonCode) && (
                    <div>
                      <span className="font-medium text-gray-700">Reason:</span>{' '}
                      <span className="text-gray-900">
                        {afterSale.reason || '-'}
                        {afterSale.reasonCode && ` (${afterSale.reasonCode})`}
                      </span>
                    </div>
                  )}
                  {afterSale.buyerMessage && (
                    <div className="col-span-2">
                      <span className="font-medium text-gray-700">Buyer Message:</span>{' '}
                      <span className="text-gray-900">{afterSale.buyerMessage}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAfterSaleId !== undefined && (
        <AddOrEditAfterSaleModal
          order={order}
          afterSaleId={modalAfterSaleId ?? undefined}
          onClose={closeModal}
          onSuccess={() => {
            closeModal();
          }}
        />
      )}
    </div>
  );
}
